import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isBottomNavTabPath, normalizePathname } from '@/lib/bottomNavVisibility';
import { resetPageScroll } from '@/lib/resetPageScroll';

function readScrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function writeScrollTop(top: number): void {
  window.scrollTo({ top, left: 0, behavior: 'instant' });
  document.documentElement.scrollTop = top;
  document.body.scrollTop = top;
}

/**
 * Main tabs share one window scroll container (not per-page overflow panes).
 * Keep each tab's scroll position independently; non-tab routes always open at top.
 */
export default function RouteScrollManager() {
  const { pathname, key } = useLocation();
  const positionsRef = useRef<Record<string, number>>({});
  const prevPathRef = useRef(normalizePathname(pathname));

  useLayoutEffect(() => {
    const prev = prevPathRef.current;
    const next = normalizePathname(pathname);

    positionsRef.current[prev] = readScrollTop();
    prevPathRef.current = next;

    if (isBottomNavTabPath(next)) {
      writeScrollTop(positionsRef.current[next] ?? 0);
      return;
    }

    // Sub-pages / auth pages: always start from the top.
    resetPageScroll();
  }, [pathname, key]);

  return null;
}
